import { Fragment } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResultCard from "@/components/ResultCard";
import ResultCardSkeleton from "@/components/ResultCardSkeleton";
import FAQSection from "@/components/FAQSection";
import ArticleCard from "@/components/ArticleCard";
import { articles } from "@/data/articles";
import GooglePreferredSource from "@/components/GooglePreferredSource";
import TiemposPlayBanner from "@/components/TiemposPlayBanner";
import StructuredData from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import DatePickerDialog from "@/components/DatePickerDialog";
import { useLotteryResults } from "@/hooks/useLotteryResults";

const Index = () => {
  const { data: results, isLoading } = useLotteryResults(0);

  const nicaraguaResults = results?.nicaraguaResults || [];
  const costaRicaResults = results?.costaRicaResults || [];

  // Convierte "11:00 AM", "1pm", "4:30 PM" a minutos para ordenar por hora
  const toMinutes = (t: string) => {
    const m = t.toLowerCase().match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
    if (!m) return 0;
    let h = parseInt(m[1] ?? "0", 10) % 12;
    if (m[3] === "pm") h += 12;
    return h * 60 + (m[2] ? parseInt(m[2], 10) : 0);
  };
  const mergedResults = [
    ...nicaraguaResults.map((r) => ({ ...r, country: "nicaragua" as const })),
    ...costaRicaResults.map((r) => ({ ...r, country: "costa-rica" as const })),
  ].sort((a, b) => toMinutes(a.time) - toMinutes(b.time));

  const faqs = [
    {
      question: "¿Qué salió hoy en los Tiempos de Costa Rica?",
      answer: "Los resultados de hoy de los Tiempos JPS de Costa Rica se muestran arriba, actualizados para la 1pm, 4:30pm y 7:30pm. Incluyen el número ganador, el tipo especial (Bolita Blanca o Reventado) y el número final."
    },
    {
      question: "¿Qué salió hoy en la Diaria Loto de Nicaragua?",
      answer: "Los resultados de la Diaria Loto de Nicaragua están disponibles arriba. De lunes a viernes hay sorteos de 11am, 3pm y 9pm, y los sábados y domingos se agrega el de las 6pm. Cada resultado incluye el número (00-99), el MultiX (JG, 2X, 3X, 5X) y el dígito final (0-9)."
    },
    {
      question: "¿Qué es el MultiX en Nicaragua?",
      answer: "El MultiX es un multiplicador especial en la Diaria Loto de Nicaragua. Puede ser JG (juego), 2X (doble), 3X (triple) o 5X (quíntuple), y multiplica tus ganancias si aciertas el número."
    },
    {
      question: "¿Qué significa Bolita Blanca o Reventado?",
      answer: "En los Tiempos JPS de Costa Rica, la Bolita Blanca y el Reventado (Bolita Roja) son indicadores especiales del sorteo. Afectan las categorías de premios y son parte importante del resultado."
    },
  ];

  const today = new Date().toLocaleDateString('es-ES', { 
    timeZone: 'America/Costa_Rica',
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <StructuredData
        title="Tiempos Hoy - Resultados de Lotería Nicaragua y Costa Rica"
        description="Resultados actualizados de la Diaria Loto de Nicaragua y Tiempos JPS de Costa Rica"
      />
      
      <div className="min-h-screen flex flex-col">
        <Navigation />
        
        <main className="flex-1 container mx-auto px-4 py-8">
          {/* Hero Section */}
          <header className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4">
              Resultados de Tiempos Hoy de la <span className="text-primary">Loto Diaria de Nicaragua</span> y{" "}
              <span className="text-primary">Nuevos Tiempos de Costa Rica</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-2">
              Diaria Loto Nicaragua 🇳🇮 y Tiempos JPS Costa Rica 🇨🇷
            </p>
            <p className="text-2xl md:text-3xl text-foreground font-semibold capitalize">{today}</p>
          </header>

          {/* Accesos rápidos */}
          <div className="grid grid-cols-2 justify-center gap-2 mb-12 sm:flex">
            <Button asChild variant="outline" size="lg" className="px-3 md:px-8">
              <Link to="/que-salio-ayer-en-la-nica">🇳🇮 Nica Ayer</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="px-3 md:px-8">
              <Link to="/que-salio-ayer-en-tiempos-jps">🇨🇷 Tica Ayer</Link>
            </Button>
            <div className="col-span-2 flex justify-center">
              <DatePickerDialog />
            </div>
          </div>

          <div className="space-y-16">
            {/* Resultados de hoy intercalados por hora (Nicaragua y Costa Rica) */}
            <section className="w-full">
              <div className="text-center mb-6">
                <h2 className="text-3xl md:text-4xl font-bold mb-2">🇳🇮 Nicaragua y 🇨🇷 Costa Rica</h2>
                <p className="text-muted-foreground">Resultados de hoy en orden de sorteo</p>
              </div>
              <div className="mx-auto grid max-w-4xl grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {isLoading ? (
                  <>
                    <ResultCardSkeleton country="nicaragua" />
                    <ResultCardSkeleton country="costa-rica" />
                    <div className="col-span-full my-4">
                      <TiemposPlayBanner />
                    </div>
                    <ResultCardSkeleton country="nicaragua" />
                    <ResultCardSkeleton country="costa-rica" />
                  </>
                ) : mergedResults.length > 0 ? (
                  mergedResults.map((r) => (
                    <Fragment key={`${r.country}-${r.time}`}>
                      <ResultCard
                        {...r}
                        time={`${r.country === "nicaragua" ? "🇳🇮" : "🇨🇷"} ${r.time}`}
                      />
                      {r.country === "costa-rica" && toMinutes(r.time) === 13 * 60 && (
                        <div className="col-span-full my-4">
                          <TiemposPlayBanner />
                        </div>
                      )}
                    </Fragment>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground col-span-full">No hay resultados disponibles para hoy.</p>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" className="bg-foreground text-background hover:bg-foreground/90">
                  <Link to="/tiempos-nica-hoy">Ver más de Nicaragua</Link>
                </Button>
                <Button asChild size="lg" className="bg-foreground text-background hover:bg-foreground/90">
                  <Link to="/tiempos-tica-hoy">Ver más de Costa Rica</Link>
                </Button>
              </div>
            </section>

            {/* Google: agregar como fuente preferida */}
            <GooglePreferredSource />
          </div>

          {/* CTA Section - Números de la Suerte — al final de los resultados */}
          <section className="text-center py-12 px-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg border-2 border-primary/20 min-h-[272px] flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="md:hidden">✨ Descubre Tus Números de la Suerte ✨</span>
              <span className="hidden md:inline">✨ Descubre Tu Número de la Suerte ✨</span>
            </h2>
            <p className="hidden md:block text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              ¿No sabes qué número jugar? Deja que el destino te guíe. Genera tu número de la suerte personalizado basado en tu fecha de nacimiento.
            </p>
            <Button asChild size="lg" className="text-lg px-8 py-6">
              <Link to="/numero-suerte">
                <span className="md:hidden">Tus Números de Suerte</span>
                <span className="hidden md:inline">Generar Mi Número de la Suerte</span>
              </Link>
            </Button>
          </section>

          <section className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-bold">📖 Artículos y Guías</h2>
              <Link to="/articulos" className="text-primary hover:underline font-medium text-sm">
                Ver todos →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((a) => (
                <ArticleCard key={a.slug} {...a} />
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <div className="mt-16">
            <FAQSection faqs={faqs} />
          </div>

          {/* Additional SEO Content */}
          <article className="mt-16 max-w-3xl mx-auto prose prose-sm">
            <h2>Sobre los Tiempos de Hoy</h2>
            <p>
              TiemposHoy.com es tu fuente confiable para consultar los resultados más recientes 
              de las loterías de Nicaragua y Costa Rica. Actualizamos los números ganadores de 
              la Diaria Loto (11am, 3pm y 9pm de lunes a viernes; con 6pm los fines de semana) y los Tiempos JPS (1pm, 4:30pm, 7:30pm) 
              inmediatamente después de cada sorteo.
            </p>
            <p>
              Ya sea que busques "nica hoy", "tica hoy" o "nuevos tiempos hoy", aquí encontrarás 
              toda la información que necesitas en un solo lugar, optimizado para dispositivos móviles 
              y fácil de consultar.
            </p>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Index;
